class MtoConfigurator extends HTMLElement {
  /** @type {Map<string, { variantId: string, price: number, label: string }>} */
  _addonSelections = new Map();
  _basePrice = 0;
  _drawerScrollY = 0;
  /** @type {Set<string>} Swatch codes currently selected in the request form */
  _selectedSwatchCodes = new Set();
  _swatchFormPending = false;
  /** @type {HTMLElement | null} */
  _drawerTrigger = null;

  connectedCallback() {
    this._form = /** @type {HTMLFormElement | null} */ (this.querySelector('.mto-form'));
    this._variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.mto-variant-id'));
    this._fabricSwatchInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.mto-prop-fabric-swatch'));
    this._cushionSwatchInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.mto-prop-cushion-swatch'));
    this._woodFinishInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.mto-prop-wood-finish'));
    this._submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('.mto-submit'));
    this._totalPriceEl = /** @type {HTMLElement | null} */ (this.querySelector('.mto-total__price'));

    this._showFabric = this.dataset.showFabric === 'true';
    this._showCushion = this.dataset.showCushion === 'true';
    this._showWood = this.dataset.showWood === 'true';
    this._basePrice = parseInt(this.dataset.basePrice ?? '0', 10);

    this._initAddons();
    this._distributeSwatches();
    this._initDrawers();
    this._initSwatchRequest();
    this._updateTotal();
    this._checkReady();

    this.addEventListener('change', (e) => {
      const input = /** @type {HTMLInputElement} */ (e.target);
      if (input.type !== 'radio') return;

      if (input.name === 'mto-fabric-swatch') {
        this.querySelectorAll('.mto-drawer[data-step-key="fabric"] .mto-swatch-card').forEach((card) => {
          card.classList.toggle('mto-swatch-card--selected', card.contains(input));
        });
        if (this._variantInput) this._variantInput.value = input.dataset.variantId ?? '';
        if (this._fabricSwatchInput) this._fabricSwatchInput.value = this._swatchPropertyValue(input);
        const gradeEl = /** @type {HTMLElement | null} */ (input.closest('.mto-fabric-grade'));
        this._basePrice = parseInt(gradeEl?.dataset.variantPrice ?? '0', 10);
        this._updateTotal();

      } else if (input.name.startsWith('mto-addon-')) {
        const groupKey = input.dataset.addonGroup ?? '';
        const picker = input.closest('.mto-addon-picker');
        picker?.querySelectorAll('.mto-addon-card').forEach((card) => {
          card.classList.toggle('mto-addon-card--selected', card.contains(input));
        });
        const propInput = /** @type {HTMLInputElement | null} */ (
          this.querySelector(`.mto-prop-addon[data-addon-group="${groupKey}"]`)
        );
        if (propInput) propInput.value = input.value;
        this._addonSelections.set(groupKey, {
          variantId: input.dataset.addonVariantId ?? '',
          price: parseInt(input.dataset.addonPrice ?? '0', 10),
          label: input.value,
        });
        this._updateTotal();

      } else {
        const picker = input.closest('.mto-swatch-picker');
        picker?.querySelectorAll('.mto-swatch-card').forEach((card) => {
          card.classList.toggle('mto-swatch-card--selected', card.contains(input));
        });
        if (input.name === 'mto-cushion-swatch' && this._cushionSwatchInput) {
          this._cushionSwatchInput.value = this._swatchPropertyValue(input);
        }
        if (input.name === 'mto-wood-finish' && this._woodFinishInput) {
          this._woodFinishInput.value = this._swatchPropertyValue(input);
        }
      }

      this._checkReady();
    });

    this._form?.addEventListener('submit', (e) => this._handleSubmit(e));
  }

  /**
   * Value saved to the cart/order line item property for a chosen swatch:
   * the swatch code followed by its name, e.g. "9850 Paloma - Almond".
   * Falls back to the code alone if the swatch has no name.
   * @param {HTMLInputElement} input
   */
  _swatchPropertyValue(input) {
    const code = input.value;
    const name = input.closest('.mto-swatch-card')?.getAttribute('title')?.trim() ?? '';
    if (!name || name === code) return code;
    return `${code} ${name}`;
  }

  _initAddons() {
    this._addonSelections = new Map();
    this.querySelectorAll('.mto-prop-addon[data-addon-group]').forEach((el) => {
      const key = /** @type {HTMLElement} */ (el).dataset.addonGroup ?? '';
      this._addonSelections.set(key, { variantId: '', price: 0, label: 'None' });
    });
  }

  _distributeSwatches() {
    const pool = /** @type {HTMLTemplateElement | null} */ (this.querySelector('#mto-swatch-pool'));
    if (!pool) return;

    /** @type {Map<string, { picker: Element, radioName: string, variantId?: string }>} */
    const gradeMap = new Map();

    this.querySelectorAll('.mto-fabric-grade[data-grade-id]').forEach((gradeEl) => {
      const gradeId = /** @type {HTMLElement} */ (gradeEl).dataset.gradeId ?? '';
      const variantId = /** @type {HTMLElement} */ (gradeEl).dataset.variantId ?? '';
      const picker = gradeEl.querySelector('.mto-swatch-picker');
      if (gradeId && picker) {
        gradeMap.set(gradeId, { picker, radioName: 'mto-fabric-swatch', variantId });
      }
    });

    this.querySelectorAll('.mto-cushion-grade[data-grade-id]').forEach((gradeEl) => {
      const gradeId = /** @type {HTMLElement} */ (gradeEl).dataset.gradeId ?? '';
      const picker = gradeEl.querySelector('.mto-swatch-picker');
      if (gradeId && picker) {
        gradeMap.set(gradeId, { picker, radioName: 'mto-cushion-swatch' });
      }
    });

    this.querySelectorAll('.mto-wood-grade[data-grade-id]').forEach((gradeEl) => {
      const gradeId = /** @type {HTMLElement} */ (gradeEl).dataset.gradeId ?? '';
      const picker = gradeEl.querySelector('.mto-swatch-picker');
      if (gradeId && picker) {
        gradeMap.set(gradeId, { picker, radioName: 'mto-wood-finish' });
      }
    });

    const fragment = pool.content;
    const cards = Array.from(fragment.querySelectorAll('.mto-swatch-card'));

    cards.forEach((card) => {
      const gradeGids = (/** @type {HTMLElement} */ (card).dataset.grade ?? '').split(' ').filter(Boolean);
      for (const [gradeId, { picker, radioName, variantId }] of gradeMap) {
        if (gradeGids.some((gid) => gid.includes(gradeId))) {
          const clone = /** @type {Element} */ (card.cloneNode(true));
          const input = clone.querySelector('input[type="radio"]');
          if (input instanceof HTMLInputElement) {
            input.name = radioName;
            if (variantId) input.dataset.variantId = variantId;
          }
          picker.appendChild(clone);
        }
      }
    });

    this._distributeSwatchesToRequest(cards);
    pool.remove();
  }

  _initDrawers() {
    this.querySelectorAll('.mto-step__cta').forEach((btn) => {
      btn.addEventListener('click', () => {
        const drawerId = /** @type {HTMLElement} */ (btn).dataset.drawerId ?? '';
        const drawer = /** @type {HTMLDialogElement | null} */ (this.querySelector(`#${drawerId}`));
        if (drawer) this._openDrawer(drawer, /** @type {HTMLElement} */ (btn));
      });
    });

    this.querySelectorAll('.mto-drawer__close').forEach((btn) => {
      btn.addEventListener('click', () => {
        const drawer = /** @type {HTMLDialogElement | null} */ (btn.closest('.mto-drawer'));
        if (drawer) this._closeDrawer(drawer);
      });
    });

    this.querySelectorAll('.mto-drawer__confirm').forEach((btn) => {
      btn.addEventListener('click', () => {
        const drawer = /** @type {HTMLDialogElement | null} */ (btn.closest('.mto-drawer'));
        if (drawer) this._confirmAndClose(drawer);
      });
    });

    this.querySelectorAll('.mto-grade-filter').forEach((filter) => {
      const buttons = Array.from(filter.querySelectorAll('.mto-grade-filter__button'));

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => this._filterGrades(/** @type {HTMLButtonElement} */ (button)));
        button.addEventListener('keydown', (event) => {
          if (!(event instanceof KeyboardEvent)) return;
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = buttons.length - 1;

          const nextButton = buttons[nextIndex];
          if (nextButton instanceof HTMLButtonElement) {
            this._filterGrades(nextButton);
            nextButton.focus();
          }
        });
      });
    });

    this.querySelectorAll('.mto-drawer').forEach((drawer) => {
      drawer.addEventListener('click', (e) => {
        if (e.target === drawer) this._closeDrawer(/** @type {HTMLDialogElement} */ (drawer));
      });
    });
  }

  /** @param {HTMLDialogElement} drawer @param {HTMLElement} [trigger] */
  _openDrawer(drawer, trigger) {
    this._drawerTrigger = trigger ?? null;
    this._drawerScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._drawerScrollY}px`;
    drawer.showModal();
    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */ (
        drawer.querySelector('.mto-grade-filter__button, .mto-swatch-card__input, .mto-addon-card input')
      )?.focus();
    });
  }

  /** @param {HTMLDialogElement} drawer */
  _closeDrawer(drawer) {
    if (!drawer.open) return;
    drawer.classList.add('mto-drawer--closing');

    const style = getComputedStyle(drawer);
    const duration = parseFloat(style.animationDuration) || 0;

    const finish = () => {
      drawer.close();
      drawer.classList.remove('mto-drawer--closing');
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this._drawerScrollY, behavior: 'instant' });
      this._drawerTrigger?.focus();
      this._drawerTrigger = null;
    };

    if (duration === 0) {
      finish();
    } else {
      drawer.addEventListener('animationend', finish, { once: true });
    }
  }

  /** @param {HTMLDialogElement} drawer */
  _confirmAndClose(drawer) {
    const stepKey = drawer.dataset.stepKey ?? '';
    const selectedInput = /** @type {HTMLInputElement | null} */ (drawer.querySelector('input[type="radio"]:checked'));
    const cta = /** @type {HTMLElement | null} */ (this.querySelector(`.mto-step__cta[data-step-key="${stepKey}"]`));
    const ctaLabel = cta?.querySelector('.mto-step__cta-label');

    if (ctaLabel && selectedInput) {
      const card = selectedInput.closest('.mto-swatch-card, .mto-addon-card');
      const displayName =
        card?.querySelector('.mto-swatch-card__name, .mto-addon-card__title')?.textContent?.trim() ??
        card?.getAttribute('title') ??
        selectedInput.value;
      ctaLabel.textContent = displayName;
      cta?.classList.add('mto-step__cta--selected');

      if (cta) {
        cta.querySelector('.mto-step__cta-swatch')?.remove();
        const imgEl = card?.querySelector('.mto-swatch-card__img');
        if (imgEl) {
          const thumb = /** @type {HTMLImageElement} */ (imgEl.cloneNode(true));
          thumb.className = 'mto-step__cta-swatch';
          thumb.removeAttribute('width');
          thumb.removeAttribute('height');
          cta.prepend(thumb);
          cta.classList.add('mto-step__cta--has-swatch');
        } else {
          const placeholderSrc = cta.dataset.selectedPlaceholderSwatchSrc ?? cta.dataset.placeholderSwatchSrc ?? '';
          const placeholder = document.createElement('img');
          placeholder.className = 'mto-step__cta-swatch mto-step__cta-swatch--placeholder';
          placeholder.src = placeholderSrc;
          placeholder.alt = '';
          placeholder.width = 56;
          placeholder.height = 56;
          placeholder.loading = 'lazy';
          if (placeholderSrc) cta.prepend(placeholder);
          cta.classList.add('mto-step__cta--has-swatch');
        }
      }
    }

    this._closeDrawer(drawer);
  }

  /** @param {HTMLButtonElement} button */
  _filterGrades(button) {
    const selectedId = button.dataset.gradeId ?? '';
    const drawer = button.closest('.mto-drawer');

    button.closest('.mto-grade-filter')?.querySelectorAll('.mto-grade-filter__button').forEach((gradeButton) => {
      const isSelected = gradeButton === button;
      gradeButton.setAttribute('aria-selected', isSelected.toString());
      /** @type {HTMLButtonElement} */ (gradeButton).tabIndex = isSelected ? 0 : -1;
    });

    drawer?.querySelectorAll('.mto-fabric-grade[data-grade-id]').forEach((grade) => {
      /** @type {HTMLElement} */ (grade).hidden = /** @type {HTMLElement} */ (grade).dataset.gradeId !== selectedId;
    });
  }

  _checkReady() {
    const hasFabric = !this._showFabric || (this._fabricSwatchInput?.value ?? '') !== '';
    const hasCushion = !this._showCushion || (this._cushionSwatchInput?.value ?? '') !== '';
    const hasWood = !this._showWood || (this._woodFinishInput?.value ?? '') !== '';
    this._setSubmitEnabled(hasFabric && hasCushion && hasWood);
  }

  /** @param {boolean} enabled */
  _setSubmitEnabled(enabled) {
    if (!this._submitButton) return;
    if (enabled) {
      this._submitButton.removeAttribute('aria-disabled');
      this._submitButton.removeAttribute('disabled');
    } else {
      this._submitButton.setAttribute('aria-disabled', 'true');
    }
  }

  _updateTotal() {
    if (!this._totalPriceEl) return;
    if (this._basePrice === 0) {
      this._totalPriceEl.textContent = '—';
      return;
    }
    let total = this._basePrice;
    for (const [, addon] of this._addonSelections) {
      total += addon?.price ?? 0;
    }
    this._totalPriceEl.textContent = this._formatMoney(total);
  }

  /** @param {number} cents */
  _formatMoney(cents) {
    const currency = window.Shopify?.currency?.active ?? 'GBP';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  }

  // ─── Swatch request ─────────────────────────────────────────────────────────

  _initSwatchRequest() {
    this.querySelector('.mto-swatch-request-open')?.addEventListener('click', () => {
      const drawerId = /** @type {HTMLElement} */ (this.querySelector('.mto-swatch-request-open'))
        .dataset.drawerId ?? '';
      const drawer = /** @type {HTMLDialogElement | null} */ (this.querySelector(`#${drawerId}`));
      if (drawer) this._openDrawer(drawer);
    });

    const requestDrawer = this.querySelector('.mto-swatch-request-drawer');
    if (!requestDrawer) return;

    requestDrawer.addEventListener('change', (e) => {
      const input = /** @type {HTMLInputElement} */ (e.target);
      if (input.name === 'mto-swatch-request') this._handleSwatchRequestToggle(input);
    });

    // Keyboard tabbing logic to move focus to the first swatch inside the grade when the grade is opened.
    requestDrawer.querySelectorAll('.mto-swatch-request-grade > summary').forEach((summary) => {
      summary.addEventListener('keydown', (e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        const details = summary.closest('details');
        if (!details) return;
        requestAnimationFrame(() => {
          if (/** @type {HTMLDetailsElement} */ (details).open) {
            /** @type {HTMLInputElement | null} */ (
              details.querySelector('.mto-swatch-card__input')
            )?.focus();
          }
        });
      });
    });

    const form = /** @type {HTMLFormElement | null} */ (
      requestDrawer.querySelector('.mto-swatch-request-form')
    );
    if (!form) return;

    // Note: Shopify's hCaptcha validates on the submit event then calls form.submit()
    // programmatically, bypassing submit event listeners entirely. Override it
    // here so we can fetch ourselves rather than letting the page navigate away.
    form.submit = () => {
      if (!this._swatchFormPending) return;
      this._swatchFormPending = false;
      this._performSwatchFetch(form);
    };

    form.addEventListener('submit', (e) => this._handleSwatchRequestSubmit(/** @type {SubmitEvent} */ (e)));
  }

  /** @param {Element[]} cards */
  _distributeSwatchesToRequest(cards) {
    const requestDrawer = this.querySelector('.mto-swatch-request-drawer');
    if (!requestDrawer) return;

    /** @type {Map<string, Element>} */
    const gradeMap = new Map();
    requestDrawer.querySelectorAll('.mto-swatch-request-picker[data-grade-id]').forEach((picker) => {
      const gradeId = /** @type {HTMLElement} */ (picker).dataset.gradeId ?? '';
      if (gradeId) gradeMap.set(gradeId, picker);
    });

    const allPicker = requestDrawer.querySelector('.mto-swatch-request-picker[data-request-all]');

    cards.forEach((card) => {
      const gradeGids = (/** @type {HTMLElement} */ (card).dataset.grade ?? '').split(' ').filter(Boolean);

      if (allPicker) {
        allPicker.appendChild(this._cloneSwatchForRequest(card));
        return;
      }

      for (const [gradeId, picker] of gradeMap) {
        if (gradeGids.some((gid) => gid.includes(gradeId))) {
          picker.appendChild(this._cloneSwatchForRequest(card));
        }
      }
    });
  }

  /** @param {Element} card */
  _cloneSwatchForRequest(card) {
    const clone = card.cloneNode(true);
    const input = /** @type {HTMLInputElement | null} */ (
      /** @type {Element} */ (clone).querySelector('input')
    );
    if (input) {
      input.type = 'checkbox';
      input.name = 'mto-swatch-request';
      delete input.dataset.variantId;
    }
    return clone;
  }

  /** @param {HTMLInputElement} input */
  _handleSwatchRequestToggle(input) {
    const MAX = 5;
    const drawer = this.querySelector('.mto-swatch-request-drawer');
    if (!drawer) return;

    const code = input.value;
    const isChecking = input.checked;

    if (isChecking) {
      if (this._selectedSwatchCodes.size >= MAX) {
        input.checked = false;
        return;
      }
      this._selectedSwatchCodes.add(code);
    } else {
      this._selectedSwatchCodes.delete(code);
    }

    const isSelected = this._selectedSwatchCodes.has(code);
    drawer
      .querySelectorAll(`input[name="mto-swatch-request"][value="${CSS.escape(code)}"]`)
      .forEach((cb) => {
        /** @type {HTMLInputElement} */ (cb).checked = isSelected;
        cb.closest('.mto-swatch-card')?.classList.toggle('mto-swatch-card--selected', isSelected);
      });

    drawer.classList.toggle('is-maxed', this._selectedSwatchCodes.size >= MAX);

    this._updateSwatchRequestStrip();
    this._updateSwatchRequestCounter();
  }

  _updateSwatchRequestStrip() {
    const drawer = this.querySelector('.mto-swatch-request-drawer');
    if (!drawer) return;

    const container = /** @type {HTMLElement | null} */ (drawer.querySelector('.mto-swatch-request-selected'));
    const strip = drawer.querySelector('.mto-swatch-request-selected__strip');
    if (!container || !strip) return;

    strip.innerHTML = '';

    if (this._selectedSwatchCodes.size === 0) {
      container.hidden = true;
      return;
    }

    container.hidden = false;

    for (const code of this._selectedSwatchCodes) {
      const refInput = /** @type {HTMLElement | null} */ (
        drawer.querySelector(`.mto-swatch-card input[value="${CSS.escape(code)}"]`)
      );
      const refCard = refInput?.closest('.mto-swatch-card');
      if (!refCard) continue;

      const nameText =
        refCard.querySelector('.mto-swatch-card__name')?.textContent?.trim() ?? code;

      const card = document.createElement('div');
      card.className = 'mto-swatch-request-selected-card';
      card.setAttribute('role', 'listitem');
      card.setAttribute('tabindex', '0');
      card.setAttribute('title', `Remove ${nameText}`);
      card.setAttribute('aria-label', `Remove ${nameText}`);
      card.dataset.code = code;

      const imgEl = refCard.querySelector('.mto-swatch-card__img');
      const fallbackEl = refCard.querySelector('.mto-swatch-card__fallback');

      if (imgEl) {
        const img = imgEl.cloneNode(true);
        /** @type {Element} */ (img).className = 'mto-swatch-request-selected-card__img';
        card.appendChild(img);
      } else if (fallbackEl) {
        const fallback = fallbackEl.cloneNode(true);
        /** @type {Element} */ (fallback).className = 'mto-swatch-request-selected-card__fallback';
        card.appendChild(fallback);
      }

      const nameEl = document.createElement('span');
      nameEl.className = 'mto-swatch-request-selected-card__name';
      nameEl.textContent = nameText;
      card.appendChild(nameEl);

      const removeEl = document.createElement('span');
      removeEl.className = 'mto-swatch-request-selected-card__remove';
      removeEl.setAttribute('aria-hidden', 'true');
      removeEl.textContent = '×';
      card.appendChild(removeEl);

      const removeHandler = () => this._removeSwatchFromRequest(code);
      card.addEventListener('click', removeHandler);
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          removeHandler();
        }
      });

      strip.appendChild(card);
    }
  }

  /** @param {string} code */
  _removeSwatchFromRequest(code) {
    const drawer = this.querySelector('.mto-swatch-request-drawer');
    if (!drawer) return;

    this._selectedSwatchCodes.delete(code);

    drawer
      .querySelectorAll(`input[name="mto-swatch-request"][value="${CSS.escape(code)}"]`)
      .forEach((cb) => {
        /** @type {HTMLInputElement} */ (cb).checked = false;
        cb.closest('.mto-swatch-card')?.classList.remove('mto-swatch-card--selected');
      });

    drawer.classList.toggle('is-maxed', this._selectedSwatchCodes.size >= 5);
    this._updateSwatchRequestStrip();
    this._updateSwatchRequestCounter();
  }

  _updateSwatchRequestCounter() {
    const drawer = this.querySelector('.mto-swatch-request-drawer');
    if (!drawer) return;

    const count = this._selectedSwatchCodes.size;
    const countEl = drawer.querySelector('.mto-swatch-request-count');
    const promptEl = /** @type {HTMLElement | null} */ (
      drawer.querySelector('.mto-swatch-request-counter__prompt')
    );

    if (countEl) countEl.textContent = String(count);
    if (promptEl) promptEl.hidden = count === 0;
  }

  /** @param {SubmitEvent} e */
  _handleSwatchRequestSubmit(e) {
    e.preventDefault();

    const drawer = this.querySelector('.mto-swatch-request-drawer');
    if (!drawer) return;

    const form = /** @type {HTMLFormElement} */ (e.currentTarget);
    const submitBtn = /** @type {HTMLButtonElement | null} */ (
      form.querySelector('.mto-swatch-request-submit')
    );
    const noticeEl = /** @type {HTMLElement | null} */ (
      form.querySelector('.mto-swatch-request-notice')
    );
    const noticeText = /** @type {HTMLElement | null} */ (
      form.querySelector('.mto-swatch-request-notice__text')
    );

    const showNotice = (/** @type {string} */ msg) => {
      if (noticeText) noticeText.textContent = msg;
      if (noticeEl) noticeEl.hidden = false;
    };

    const hideNotice = () => {
      if (noticeEl) noticeEl.hidden = true;
    };

    hideNotice();

    if (this._selectedSwatchCodes.size === 0) {
      showNotice('Please select at least one swatch before submitting.');
      return;
    }

    // Validate required fields
    let isValid = true;
    form.querySelectorAll('[required]').forEach((field) => {
      if (field instanceof HTMLInputElement && !field.value.trim()) {
        field.classList.add('is-invalid');
        isValid = false;
      } else if (field instanceof HTMLInputElement) {
        field.classList.remove('is-invalid');
      }
    });

    if (!isValid) {
      showNotice('Please fill in all required fields.');
      /** @type {HTMLInputElement | null} */ (form.querySelector('.is-invalid'))?.focus();
      return;
    }

    // Build swatch name list
    const swatchNames = [];
    for (const code of this._selectedSwatchCodes) {
      const refCard = drawer
        .querySelector(`.mto-swatch-card input[value="${CSS.escape(code)}"]`)
        ?.closest('.mto-swatch-card');
      swatchNames.push(
        refCard?.querySelector('.mto-swatch-card__name')?.textContent?.trim() ?? code
      );
    }

    // Write dynamic values directly onto the DOM hidden inputs so FormData picks
    // them up correctly in all paths — our fetch AND any hCaptcha form.submit().
    const tempData = new FormData(form);
    const bodyText = [
      `Swatch Request — ${this.dataset.productTitle ?? ''}`,
      '',
      `Requested swatches: ${swatchNames.join(', ')}`,
      '',
      `Phone: ${tempData.get('contact[Phone]') ?? ''}`,
      `Address: ${[
        tempData.get('contact[Address Line 1]'),
        tempData.get('contact[Address Line 2]'),
        tempData.get('contact[Town / City]'),
        tempData.get('contact[Postcode]'),
      ]
        .filter(Boolean)
        .join(', ')}`,
    ].join('\n');

    const swatchesInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="contact[Requested Swatches]"]')
    );
    const bodyInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="contact[body]"]')
    );
    if (swatchesInput) swatchesInput.value = swatchNames.join(', ');
    if (bodyInput) bodyInput.value = bodyText;

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';
    }

    this._swatchFormPending = true;
    if (!form.querySelector('[name="h-captcha-response"]')) {
      form.submit();
    }
  }

  /** @param {HTMLFormElement} form */
  async _performSwatchFetch(form) {
    const drawer = this.querySelector('.mto-swatch-request-drawer');
    const submitBtn = /** @type {HTMLButtonElement | null} */ (
      form.querySelector('.mto-swatch-request-submit')
    );
    const noticeEl = /** @type {HTMLElement | null} */ (
      form.querySelector('.mto-swatch-request-notice')
    );
    const noticeText = /** @type {HTMLElement | null} */ (
      form.querySelector('.mto-swatch-request-notice__text')
    );

    const showNotice = (/** @type {string} */ msg) => {
      if (noticeText) noticeText.textContent = msg;
      if (noticeEl) noticeEl.hidden = false;
    };

    try {

      const formData = new FormData(form);

      if (!formData.get('form_key')) {
        const csrfFormKey = this.querySelector('#mto-swatch-csrf-form input[name="form_key"]')?.value;
        if (csrfFormKey) formData.set('form_key', csrfFormKey);
      }
      // redirect: 'manual' stops fetch from following Shopify's 302 redirect.
      // An opaque redirect response means Shopify accepted the submission.
      const res = await fetch('/contact', { method: 'POST', body: formData, redirect: 'manual' });
      const succeeded = res.type === 'opaqueredirect';

      if (succeeded) {
        form.hidden = true;
        const successEl = /** @type {HTMLElement | null} */ (
          drawer?.querySelector('.mto-swatch-request-success')
        );
        if (successEl) successEl.hidden = false;
      } else {
        throw new Error(`Unexpected response (status: ${res.status}, type: ${res.type})`);
      }
    } catch (err) {
      console.error('[mto-swatch-request]', err);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Send swatch request';
      }
      showNotice('Something went wrong. Please try again or contact us directly.');
    }
  }

  /** @param {SubmitEvent} e */
  async _handleSubmit(e) {
    e.preventDefault();
    if (this._submitButton?.getAttribute('aria-disabled') === 'true') return;

    this._setSubmitEnabled(false);
    const originalLabel = this._submitButton?.textContent ?? 'Add to cart';
    if (this._submitButton) this._submitButton.textContent = 'Adding…';

    const configId = Math.random().toString(36).slice(2, 10);
    const productTitle = this.dataset.productTitle ?? '';
    const fabricValue = this._fabricSwatchInput?.value ?? '';
    const addonFor = [productTitle, fabricValue].filter(Boolean).join(' — ');

    try {
      const formData = new FormData(/** @type {HTMLFormElement} */ (this._form));
      formData.delete('properties[]');
      formData.set('properties[_config_id]', configId);

      const mainRes = await fetch('/cart/add.js', { method: 'POST', body: formData });
      if (!mainRes.ok) {
        const err = await mainRes.json().catch(() => ({}));
        throw new Error(/** @type {any} */ (err).description ?? `Cart add failed (${mainRes.status})`);
      }

      for (const [, addon] of this._addonSelections) {
        if (!addon?.variantId) continue;
        const addonRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: Number(addon.variantId),
            quantity: 1,
            properties: {
              'Applied to': addonFor,
              '_config_id': configId,
            },
          }),
        });
        if (!addonRes.ok) {
          const err = await addonRes.json().catch(() => ({}));
          throw new Error(/** @type {any} */ (err).description ?? `Add-on cart add failed (${addonRes.status})`);
        }
      }

      window.location.href = '/cart';
    } catch (err) {
      console.error('[mto-configurator]', err);
      this._setSubmitEnabled(true);
      if (this._submitButton) this._submitButton.textContent = originalLabel;
    }
  }
}

customElements.define('mto-configurator', MtoConfigurator);
